import { inject, Injectable } from "@angular/core";
import {
  NgbCalendar,
  NgbCalendarBuddhist,
  NgbCalendarGregorian,
  NgbDate,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbDatepickerI18n,
} from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { TranslationService } from "src/app/i18n";

type DatePeriod = "y" | "m" | "d";

export function getDatepickerYearOffset(
  currentLanguage: string | undefined,
  fallbackLanguage: string,
  buddhistYearOffset: number
): number {
  const language =
    currentLanguage != null && currentLanguage !== ""
      ? currentLanguage
      : fallbackLanguage;

  return language === "th" ? buddhistYearOffset : 0;
}

export function toNgbDate(date: Date, yearOffset: number): NgbDate {
  return new NgbDate(
    date.getFullYear() + yearOffset,
    date.getMonth() + 1,
    date.getDate()
  );
}

export function shiftNgbDateYear(
  value: NgbDate | null,
  previousYearOffset: number,
  currentYearOffset: number
): NgbDate | null {
  return value == null
    ? null
    : new NgbDate(
        value.year - previousYearOffset + currentYearOffset,
        value.month,
        value.day
      );
}

export function serializeNgbDate(
  value: NgbDate,
  yearOffset: number
): string {
  const year = value.year - yearOffset;
  const month = String(value.month).padStart(2, "0");
  const day = String(value.day).padStart(2, "0");

  return String(new Date([year, month, day].join("-")));
}

@Injectable()
export class LocaleAwareCalendar extends NgbCalendar {
  private readonly translationService = inject(TranslationService);
  private readonly translateService = inject(TranslateService);
  private readonly buddhist = new NgbCalendarBuddhist();
  private readonly gregorian = new NgbCalendarGregorian();

  private get calendar(): NgbCalendar {
    const language =
      this.translateService.currentLang ||
      this.translationService.getSelectedLanguage();
    return language === "th" ? this.buddhist : this.gregorian;
  }

  getDaysPerWeek(): number {
    return this.calendar.getDaysPerWeek();
  }

  getMonths(year?: number): number[] {
    return this.calendar.getMonths(year);
  }

  getWeeksPerMonth(): number {
    return this.calendar.getWeeksPerMonth();
  }

  getWeekday(date: NgbDate): number {
    return this.calendar.getWeekday(date);
  }

  getNext(date: NgbDate, period?: DatePeriod, number?: number): NgbDate {
    return this.calendar.getNext(date, period, number);
  }

  getPrev(date: NgbDate, period?: DatePeriod, number?: number): NgbDate {
    return this.calendar.getPrev(date, period, number);
  }

  getWeekNumber(week: readonly NgbDate[], firstDayOfWeek: number): number {
    return this.calendar.getWeekNumber(week, firstDayOfWeek);
  }

  getToday(): NgbDate {
    return this.calendar.getToday();
  }

  isValid(date?: NgbDate | null): boolean {
    return this.calendar.isValid(date);
  }
}

@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = "/";

  parse(value: string): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        day: parseInt(date[0], 10),
        month: parseInt(date[1], 10),
        year: parseInt(date[2], 10),
      };
    }
    return null;
  }

  format(date: NgbDateStruct | null): string {
    return date
      ? String(date.day).padStart(2, "0") +
          this.DELIMITER +
          String(date.month).padStart(2, "0") +
          this.DELIMITER +
          date.year
      : "";
  }
}

const I18N_VALUES = {
  th: {
    weekdays: ["จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส.", "อา."],
    months: [
      "มกราคม",
      "กุมภาพันธ์",
      "มีนาคม",
      "เมษายน",
      "พฤษภาคม",
      "มิถุนายน",
      "กรกฎาคม",
      "สิงหาคม",
      "กันยายน",
      "ตุลาคม",
      "พฤศจิกายน",
      "ธันวาคม",
    ],
  },
  en: {
    weekdays: ["M", "T", "W", "TH", "F", "SA", "S"],
    months: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
  },
};

@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
  #translate = inject(TranslationService);
  #ngxTranslate = inject(TranslateService);

  private get locale(): "th" | "en" {
    const language =
      this.#ngxTranslate.currentLang || this.#translate.getSelectedLanguage();
    return language === "th" ? "th" : "en";
  }

  getWeekdayShortName(weekday: number): string {
    return I18N_VALUES[this.locale].weekdays[weekday - 1];
  }

  getWeekdayLabel(
    weekday: number,
    _width?: "long" | "short" | "narrow"
  ): string {
    return this.getWeekdayShortName(weekday);
  }

  getMonthShortName(month: number): string {
    return I18N_VALUES[this.locale].months[month - 1];
  }

  getMonthFullName(month: number): string {
    return I18N_VALUES[this.locale].months[month - 1];
  }

  getDayAriaLabel(date: { day: number; month: number; year: number }): string {
    return `${date.day}-${date.month}-${date.year}`;
  }
}
